import {
  Component,
  Inject,
  Injector,
  OnDestroy
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { assets } from '../../../core/constants/assets.const';
import { LoginComponent } from '../login/login.component';
import { VerificationComponent } from '../verification/verification.component';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { Subject, takeUntil } from 'rxjs';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseClass } from '../../../shared/baseClass/baseClass';
import { emailErrors } from '../../../core/constants/commonErrors.const';
import { countryCode } from '../../../models/countryCode.model';
import { CountryCodeConst } from '../../../core/constants/countryCode.const';
import { OtpType, SignUpType } from '../../../core/constants/keys.constant';
import { AuthService } from '../../../services/auth.service';
import { profileSetupStatus } from '../../../core/constants/booking.const';
import { AnalyticsService } from '../../../services/analytics.service';
import { guestLoginData } from '../../../models/auth.model';
import { responseStatus } from '../../../core/constants/responseStatus.const';
@Component({
  selector: 'xplora-website-signup',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDialogModule,
    TranslateModule,
    MatTabsModule,
    NgxMatIntlTelInputComponent
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})
export class SignupComponent extends BaseClass implements OnDestroy {
  protected countryCodeObject: countryCode = CountryCodeConst;
  protected selectedTab = 0;
  protected assetsConst = assets;
  private destroy: Subject<boolean> = new Subject<boolean>();
  protected signUpFormGroup: FormGroup;
  protected submitted = false;
  protected emailErrors = emailErrors;
  protected signUpType = SignUpType.Email;
  protected profileSetupStatus = profileSetupStatus;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<SignupComponent>,
    private _authService: AuthService,
    private gs: AnalyticsService,
    @Inject(MAT_DIALOG_DATA) private data?: guestLoginData
  ) {
    super(injector);
    this.signUpFormGroup = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      phoneNo: [
        { value: '',
          disabled: true }, [Validators.required]
      ],
      dialCode: [
        { value: '',
          disabled: true }
      ]
    });
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.submitted = false;
    this.selectedTab = event.index;
    if (this.selectedTab === 0) {
      this.signUpType = SignUpType.Email;
      this.signUpFormGroup.get('email')?.enable();
      this.signUpFormGroup.get('phoneNo')?.disable();
      this.signUpFormGroup.get('dialCode')?.disable();
      this.signUpFormGroup.get('email')?.updateValueAndValidity();
      this.signUpFormGroup.get('phoneNo')?.updateValueAndValidity();
      this.signUpFormGroup.get('dialCode')?.updateValueAndValidity();
    } else {
      this.signUpType = SignUpType.Phone;
      this.signUpFormGroup.get('email')?.disable();
      this.signUpFormGroup.get('phoneNo')?.enable();
      this.signUpFormGroup.get('dialCode')?.enable();
      this.signUpFormGroup.get('email')?.updateValueAndValidity();
      this.signUpFormGroup.get('phoneNo')?.updateValueAndValidity();
      this.signUpFormGroup.get('dialCode')?.updateValueAndValidity();
    }
  }

  openLoginModal(): void {
    const dialogRef = this.dialog.open(LoginComponent, {
      panelClass: ['modal', 'auth_modal']
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  openVerificationModal(): void {
    this.submitted = true;
    this.gs.pushToDataLayer({event: 'signup'})
    if (this.signUpFormGroup.valid) {
      const modifiedValue = this.patchValues();

      modifiedValue.profileStepsCompleted = this.profileSetupStatus.signup;
      
      // Add membership_id if this signup is from trial flow
      if (this.data?.trialFlow && this.data?.membershipPlan?.id) {
        (modifiedValue as any).membership_id = this.data.membershipPlan.id;
      }
      
      this.spinnerService.addToLoader('userSignUp');
      
      this._authService
        .userSignUp(modifiedValue)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: () => {
            this.spinnerService.removeFromLoader('userSignUp');
            const verificationDialogRef = this.dialog.open(VerificationComponent, {
              panelClass: ['modal', 'auth_modal'],
              data: {
                profileSetup: true,
                data: modifiedValue,
                signUpType: this.signUpType,
                otpType: OtpType.SignUp,
                trialFlow: this.data?.trialFlow,
                membershipPlan: this.data?.membershipPlan,
                source: this.data?.source
              }
            });
            
            // Listen for verification completion
            verificationDialogRef.afterClosed().subscribe((verificationResult: any) => {
              if (verificationResult === responseStatus.SUCCESS || (verificationResult && verificationResult.status === responseStatus.SUCCESS)) {
                if (this.data?.trialFlow && this.data?.membershipPlan) {
                  this.dialogRef.close({
                    status: responseStatus.SUCCESS,
                    trialFlow: true,
                    membershipPlan: this.data.membershipPlan,
                    source: this.data.source
                  });
                } else {
                  this.dialogRef.close(responseStatus.SUCCESS);
                }
              } else {
                this.dialogRef.close();
              }
            });
          },
          error: () => {
            this.spinnerService.removeFromLoader('userSignUp');
          }
        });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countryChangedEvent(event: any): void {
    this.countryCodeObject = event;
  }

  patchValues(): any {
    // eslint-disable-next-line prefer-const, @typescript-eslint/no-explicit-any
    let value: any;
    const patchValueToCountryCode = (): void => {
      let tempCountryCode = this.signUpFormGroup.get('dialCode')?.value;
      if (tempCountryCode) {
        tempCountryCode = tempCountryCode.split('');
        tempCountryCode.shift();
        tempCountryCode = tempCountryCode.join('');
      }
      if (tempCountryCode !== this.countryCodeObject.dialCode) {
        this.signUpFormGroup.patchValue({
          dialCode: `+${this.countryCodeObject.dialCode}`
        });
      }
    };
    const patchValueToPhoneField = (): void => {
      let phone = this.signUpFormGroup.get('phoneNo')?.value;
      if (
        phone &&
        phone.includes(this.signUpFormGroup.get('dialCode')?.value)
      ) {
        phone = phone.split(this.signUpFormGroup.get('dialCode')?.value);
        if (value.phoneNo !== undefined) {
          value.phoneNo = phone[1].trim();
        }
      }
    };
    patchValueToCountryCode();
    value = this.signUpFormGroup['value'];
    patchValueToPhoneField();
    return value;
  }

  ngOnDestroy(): void {
    this.destroy.complete();
    this.destroy.unsubscribe();
  }
}
